<template>
  <section
    :class="sectionBgClass"
    :style="backgroundColorStyle"
    class="w-full overflow-hidden"
  >
    <div :class="[data?.containerFull ? 'w-full' : 'container px-0 lg:px-4']">
      <!-- title -->
      <UiHeading
        v-if="data?.heading"
        :pin="data?.heading?.pin"
        :title="data?.heading?.title"
        :description="data?.heading?.description"
        :button="data?.heading?.button"
        :position="data?.heading?.position"
        :color="data?.heading?.color"
        v-aos="animate && 'zoom-out'"
      />
      <!-- title -->

      <!-- Slider -->
      <template v-if="shouldShowSlider">
        <div v-aos="animate && 'fade-up'" class="w-full block lg:hidden">
          <PluginsCarousel
            :data="data?.items"
            :pagination="true"
            :navigation="false"
            :per="data?.column === 'column-4' ? 4 : 3"
            type="Card"
            class="cards-slider"
            :class="carouselClass"
          >
            <template v-slot="{ slide }">
              <UiCardNews
                :pin="slide?.pin"
                :title="slide?.title"
                :description="slide?.description"
                :link="buildNewsDetailLink(slide)"
                :media="slide?.media"
                :position="data?.position"
                :mediaSize="data?.mediaSize"
                :background="data?.backgroundColor"
                :objectFit="data?.objectFit"
                class="h-full"
              />
            </template>
          </PluginsCarousel>
        </div>
      </template>
      <!-- Slider -->

      <!-- Non Slider -->

      <template v-else>
        <!-- Mobile/Tablet Stack (Non-Slider) -->
        <div class="flex flex-col gap-6 lg:hidden px-4">
          <template v-for="(item, index) in data?.items" :key="item?.id || index">
            <UiCardNews
              v-aos="animate && 'fade-up'"
              :pin="item?.pin"
              :title="item?.title"
              :description="item?.description"
              :link="buildNewsDetailLink(item)"
              :media="item?.media"
              :mediaSize="data?.mediaSize"
              :background="data.backgroundColor"
            />
          </template>
        </div>

        <!-- Desktop Split Layout (50/50) -->
        <div class="hidden lg:flex items-stretch gap-6 xl:gap-8">
          <!-- Featured Card (Left - 50%) -->
          <div class="w-1/2 flex flex-col">
            <UiCardNews
              v-if="data?.items?.length > 0"
              v-aos="animate && 'slide-right'"
              :pin="data?.items[0]?.pin"
              :title="data?.items[0]?.title"
              :description="data?.items[0]?.description"
              :link="buildNewsDetailLink(data?.items[0])"
              :media="data?.items[0]?.media"
              :mediaSize="data?.mediaSize"
              :background="data.backgroundColor"
              class="h-full"
            />
          </div>
          <!-- Stacked Cards (Right - 50%) -->
          <div class="w-1/2 flex flex-col justify-between gap-4 xl:gap-5">
            <template v-if="data?.items?.length > 1">
              <template v-for="(item, index) in data?.items.slice(1, 4)" :key="item?.id || index">
                <UiCardNews
                  v-aos="animate && 'slide-left'"
                  :pin="item?.pin"
                  :title="item?.title"
                  :description="item?.description"
                  :link="buildNewsDetailLink(item)"
                  :media="item?.media"
                  :mediaSize="data?.mediaSize"
                  :background="data.backgroundColor"
                  cardType="horizontal"
                  class="flex-1"
                />
              </template>
            </template>
          </div>
        </div>
      </template>

      <!-- Non Slider -->

      <!-- Button -->
      <div
        v-if="data?.showMoreLink && data?.showMoreLink?.length > 0"
        class="flex mt-4 md:mt-6 lg:mt-8 xl:mt-10 justify-center"
      >
        <AtomsButton
          :label="$t('Show More')"
          :link="data?.showMoreLink"
          :color="buttonColor"
          size="medium"
          :enabled="true"
        />
      </div>
      <!-- Button -->
    </div>
  </section>
</template>
<script setup>
import { computed } from "vue";
import { useLocalePath } from "#i18n";
import { useIsMobile } from "~/composables/utils";
import { sectionOuterSpacingClass } from "~/composables/sectionSpacing";
import { formatLocaleDate } from "~/composables/dateFormatter";
import { getBrandBgClass } from "~/composables/utils";
const props = defineProps({
  data: { type: Object },
  animate: { type: Boolean, default: false },
});

const { isMobile } = useIsMobile();
const localePath = useLocalePath();
const sectionBgClass = computed(() => {
  const bg = props.data?.backgroundColor;
  const brandClass = getBrandBgClass(bg);
  const spacingClass = sectionOuterSpacingClass(props.data);
  return [brandClass ? `\\` + (props.data?.backgroundColor !== 'transparent' ? ' py-10 lg:py-20' : '') : '', spacingClass];
});

const backgroundColorStyle = computed(() => {
  const bg = props.data?.backgroundColor;
  if (bg && bg.startsWith("#")) {
    return { backgroundColor: bg };
  }
  return {};
});

const carouselClass = computed(() => {
  const bg = props.data?.backgroundColor;
  return (bg === 'first' || bg === 'second' || bg === 'dark-first' || bg === 'dark-second') ? '' : 'non-bg';
});

const buttonColor = computed(() => props.data?.heading?.color === 'white' ? 'white-outline' : 'black-outline');

const newsBasePath = computed(() => {
  const localized = localePath({ name: "news" });
  return localized || "/haberler";
});

const shouldShowSlider = computed(() => {
  return isMobile.value;
});

const buildNewsDetailLink = (item = {}) => {
  const slug = item?.slug;
  if (!slug) {
    return newsBasePath.value;
  }
  return localePath({ name: "news-slug", params: { slug } });
};

const formatDate = (value) => formatLocaleDate(value);
</script>
